import React from "react";
import {
    BulkActionId,
    LOCKED_TRANSACTIONS_DISABLED_TOOLTIP,
} from "@components/transactions/TransactionsBulkActions/lib";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { TooltipBodyProps } from "@design-system/Tooltip/Tooltip.types";
import { HStack } from "@design-system/HStack/HStack";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import actionBarStyles from "../../../../components/transactions/TransactionsBulkActions/TransactionsActionBar/TransactionsActionBar.module.scss";

interface BulkAction {
    id: BulkActionId;
    name: string | React.ReactNode;
    /** Hotkey string for the chip beside the label, e.g. `delete` or `d`. */
    shortcut?: string;
    requiresCtrlOrMeta?: boolean;
    ref: React.RefObject<HTMLButtonElement | null>;
    icon?: React.ReactNode;
    tooltip?: string;
}

/** Action label with its shortcut chip pushed to the far end. */
const ShortcutLabel = ({
    label,
    shortcut,
}: {
    label: React.ReactNode;
    shortcut?: string;
}) => (
    <HStack
        gap={4}
        align="center"
        justify="between"
        className={actionBarStyles.shortcutLabel}
    >
        {label}
        {shortcut ? <Shortcut keys={shortcut} /> : null}
    </HStack>
);

/** The action bar tooltip: the lock reason, or the label with its hotkey. */
const actionBarTooltipBody = ({
    action,
    disabledDueToLockedTransactions,
}: {
    action: BulkAction;
    disabledDueToLockedTransactions: boolean;
}): TooltipBodyProps => {
    if (disabledDueToLockedTransactions) {
        return { content: LOCKED_TRANSACTIONS_DISABLED_TOOLTIP };
    }
    const label = action.tooltip ?? action.name;
    if (action.shortcut) {
        return { content: label, shortcut: action.shortcut };
    }
    return { content: label };
};

type ActionButtonVariant = "actionBar" | "dropdown";

interface ActionButtonProps {
    action: BulkAction;
    variant?: ActionButtonVariant;
    disabled?: boolean;
    disabledDueToLockedTransactions?: boolean;
    onClick?: () => void;
    children?: React.ReactNode;
}

export const ActionButton: React.FC<ActionButtonProps> = ({
    action,
    variant = "actionBar",
    disabled = false,
    disabledDueToLockedTransactions = false,
    onClick,
    children,
}) => {
    const handleClick = onClick ?? (() => action.ref.current?.click());

    if (variant === "dropdown") {
        return (
            <Tooltip
                content={
                    disabledDueToLockedTransactions
                        ? LOCKED_TRANSACTIONS_DISABLED_TOOLTIP
                        : undefined
                }
            >
                <Button
                    ref={action.ref}
                    onClick={handleClick}
                    variant="secondary"
                    size="md"
                    className="justify-content-start"
                    block
                    disabled={disabled}
                >
                    {children || (
                        <ShortcutLabel
                            shortcut={action.shortcut}
                            label={action.name}
                        />
                    )}
                </Button>
            </Tooltip>
        );
    }

    return (
        <Tooltip
            {...actionBarTooltipBody({
                action,
                disabledDueToLockedTransactions,
            })}
        >
            <button
                ref={action.ref}
                className={actionBarStyles.button}
                disabled={disabled}
                onClick={handleClick}
            >
                {children || action.name}
            </button>
        </Tooltip>
    );
};
